import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { RootState } from '../store';
import { setForms } from '../store/formsSlice';
import Layout from '../components/Layout';
import { taxFormsApi, aiProfileApi } from '../services/api';
import { FileUp, ClipboardList, FileText, CheckCircle, Clock, AlertCircle, Download, ArrowRight } from 'lucide-react';

interface RecentDocument {
  id: string;
  name: string;
  uploadedAt: string;
  status: 'uploaded' | 'processing' | 'error';
  documentType: string;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { documents } = useSelector((state: RootState) => state.documents);
  const { currentStep, totalSteps, answers: questionnaireAnswers } = useSelector((state: RootState) => state.questionnaire);
  const { forms } = useSelector((state: RootState) => state.forms);
  const selectedTaxYear = useSelector((state: RootState) => state.analysis.selectedTaxYear);
  interface AiProfileSummary {
    last_updated?: string;
    personal?: {
      filing_status_history?: Array<{ status?: string }>;
      state_of_residence?: string;
    };
    income_patterns?: {
      primary_income_type?: string;
      income_trend?: Array<{ year: number; agi?: number }>;
    };
    deduction_preferences?: {
      preferred_method?: string;
    };
  }
  const [aiProfile, setAiProfile] = useState<AiProfileSummary | null>(null);

  useEffect(() => {
    taxFormsApi.getLatestForms(selectedTaxYear)
      .then((res) => dispatch(setForms(res.data.forms || [])))
      .catch(() => {
        // No forms yet — render empty state
      });
    aiProfileApi.getProfile()
      .then((res) => setAiProfile(res.data))
      .catch(() => {
        // No profile yet — don't render the profile summary
      });
  }, [dispatch, selectedTaxYear]);

  const recentDocuments: RecentDocument[] = documents.slice(0, 5).map((doc) => ({
    id: doc.id,
    name: doc.fileName,
    uploadedAt: doc.uploadDate,
    status: doc.status,
    documentType: doc.documentType,
  }));

  const getStatusBadge = (status: RecentDocument['status']) => {
    switch (status) {
      case 'uploaded':
        return (
          <span className="flex items-center space-x-1 text-xs text-gray-500">
            <CheckCircle className="w-3 h-3 text-green-500" />
            <span>Uploaded</span>
          </span>
        );
      case 'processing':
        return (
          <span className="flex items-center space-x-1 text-xs text-gray-500">
            <Clock className="w-3 h-3 text-amber-500" />
            <span>Processing</span>
          </span>
        );
      default:
        return (
          <span className="flex items-center space-x-1 text-xs text-gray-500">
            <AlertCircle className="w-3 h-3 text-red-500" />
            <span>Error</span>
          </span>
        );
    }
  };

  const questionnaireStarted = !!questionnaireAnswers.filingStatus;
  const questionnaireProgress = questionnaireStarted
    ? Math.round((currentStep / totalSteps) * 100)
    : 0;

  return (
    <Layout>
      {/* Welcome */}
      <div className="mb-10">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          Welcome back, {user?.firstName || 'there'}
        </h1>
        <p className="text-sm text-gray-500">
          Tax Year {selectedTaxYear} &middot; {documents.length} document{documents.length !== 1 ? 's' : ''} uploaded
        </p>
      </div>

      {/* Progress Steps */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <button
          onClick={() => navigate('/upload')}
          className="text-left p-5 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-all group"
        >
          <div className="flex items-center justify-between mb-3">
            <FileUp className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
            {documents.length > 0 ? (
              <span className="text-xs text-green-600 font-medium">{documents.length} uploaded</span>
            ) : (
              <span className="text-xs text-gray-400">Not started</span>
            )}
          </div>
          <h3 className="text-sm font-semibold text-gray-900 mb-0.5">Upload Documents</h3>
          <p className="text-xs text-gray-500">W-2s, 1099s, and other tax forms</p>
        </button>

        <button
          onClick={() => navigate('/questionnaire')}
          className="text-left p-5 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-all group"
        >
          <div className="flex items-center justify-between mb-3">
            <ClipboardList className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
            {questionnaireStarted ? (
              <span className="text-xs text-amber-600 font-medium">{questionnaireProgress}% complete</span>
            ) : (
              <span className="text-xs text-gray-400">Not started</span>
            )}
          </div>
          <h3 className="text-sm font-semibold text-gray-900 mb-0.5">Questionnaire</h3>
          <p className="text-xs text-gray-500">Income, deductions, and life changes</p>
        </button>

        <button
          onClick={() => navigate('/review')}
          className="text-left p-5 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-all group"
        >
          <div className="flex items-center justify-between mb-3">
            <FileText className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
            {forms.length > 0 ? (
              <span className="text-xs text-green-600 font-medium">{forms.length} form{forms.length !== 1 ? 's' : ''} ready</span>
            ) : (
              <span className="text-xs text-gray-400">Pending</span>
            )}
          </div>
          <h3 className="text-sm font-semibold text-gray-900 mb-0.5">Tax Review</h3>
          <p className="text-xs text-gray-500">Review results and download forms</p>
        </button>
      </div>

      {/* AI Profile Summary */}
      {aiProfile && aiProfile.last_updated && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Your Tax Profile</h2>
          <div className="rounded-xl border border-gray-100 p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {(aiProfile.personal?.filing_status_history?.length ?? 0) > 0 && aiProfile.personal?.filing_status_history && (
                <div>
                  <p className="text-[11px] text-gray-400 uppercase tracking-wide">Filing Status</p>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">
                    {aiProfile.personal.filing_status_history[aiProfile.personal.filing_status_history.length - 1]?.status?.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  </p>
                </div>
              )}
              {aiProfile.personal?.state_of_residence && (
                <div>
                  <p className="text-[11px] text-gray-400 uppercase tracking-wide">State</p>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">{aiProfile.personal.state_of_residence}</p>
                </div>
              )}
              {aiProfile.income_patterns?.primary_income_type && (
                <div>
                  <p className="text-[11px] text-gray-400 uppercase tracking-wide">Income Type</p>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">{aiProfile.income_patterns.primary_income_type}</p>
                </div>
              )}
              {aiProfile.deduction_preferences?.preferred_method && (
                <div>
                  <p className="text-[11px] text-gray-400 uppercase tracking-wide">Deductions</p>
                  <p className="text-sm font-medium text-gray-900 mt-0.5 capitalize">{aiProfile.deduction_preferences.preferred_method}</p>
                </div>
              )}
            </div>
            {(aiProfile.income_patterns?.income_trend?.length ?? 0) > 0 && aiProfile.income_patterns?.income_trend && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-2">Income History</p>
                <div className="flex space-x-6">
                  {aiProfile.income_patterns.income_trend.slice(-3).map((t) => (
                    <div key={t.year} className="text-sm">
                      <span className="text-gray-400">{t.year}:</span>{' '}
                      <span className="font-medium text-gray-900">
                        {t.agi ? `$${Number(t.agi).toLocaleString()}` : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tax Forms */}
      {forms.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Generated Forms</h2>
          <div className="rounded-xl border border-gray-100 divide-y divide-gray-100">
            {forms.map((form) => (
              <div key={form.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div>
                  <p className="text-sm font-medium text-gray-900">{form.formType.replace(/_/g, ' ')}</p>
                  <p className="text-xs text-gray-400">v{form.version} &middot; {form.generatedAt ? new Date(form.generatedAt).toLocaleDateString() : ''}</p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const res = await taxFormsApi.downloadForm(form.id);
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = form.fileName;
                      a.click();
                      window.URL.revokeObjectURL(url);
                    } catch {
                      // Swallow download failure — user can retry
                    }
                  }}
                  className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-800 transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Download</span>
                </button>
              </div>
            ))}
            <div className="p-3 px-4">
              <button
                onClick={() => navigate('/review')}
                className="text-xs text-gray-500 hover:text-gray-800 font-medium flex items-center space-x-1"
              >
                <span>View full tax review</span>
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recent Documents */}
      <div>
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
          {recentDocuments.length > 0 ? 'Recent Documents' : 'Get Started'}
        </h2>
        <div className="rounded-xl border border-gray-100">
          {recentDocuments.length === 0 ? (
            <div className="p-10 text-center">
              <FileUp className="w-8 h-8 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-600 font-medium mb-1">No documents uploaded yet</p>
              <p className="text-xs text-gray-400 mb-5">
                Upload your tax documents to get started
              </p>
              <button
                onClick={() => navigate('/upload')}
                className="inline-flex items-center space-x-2 px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-full text-sm font-medium transition-colors"
              >
                <FileUp className="w-3.5 h-3.5" />
                <span>Upload Documents</span>
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {recentDocuments.map((doc) => (
                <div key={doc.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {doc.documentType} &middot; {new Date(doc.uploadedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div>{getStatusBadge(doc.status)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
